import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { PAGE_PATHS } from "./page-paths";
import { projectStartDate } from "../config";
import { useAuth } from "../utils/hooks/useAuth";
import { UserOnboardingStats } from "./Home/VLEStatsFragment";
import usePromise from "../utils/hooks/usePromise";
import { services } from "../services";
import Select from "react-select";
import {
  District,
  PaymentRequest,
  State,
  VLE,
} from "@iibf/shared-resources/models/db";
import { File } from "@iibf/shared-resources/models/file";
import Loader from "../assets/animated-images/loader";
import { useNavigate } from "react-router-dom";

import DataTable, { TableColumn } from "react-data-table-component";
import FormContext, { RegistrationFormContextShape } from "../contexts/form";
import {
  IDENTITY_PROOF_CODE,
  IDENTITY_PROOF_NAME,
  QUALIFICATION_CODE,
  QUALIFICATION_NAME,
} from "@iibf/shared-resources/consts";
import { calculateStatistics } from "./Home/calculate-statistics";
import { Navbar } from "./Home/Navbar";
import { useURLState } from "../utils/url-state";
import {
  ObjectKeyPath,
  ObjectKeyPathValueType,
  setObjectValue,
} from "../utils/func";
import { GetCandidatesRequestInputDTO } from "../utils/custom-dto/get-candidates";
import moment from "moment";
import { UserListActionsFragment } from "./Home/UserListActionsFragment";
import { downloadFileFromURL } from "../utils/file";
import { MonthYearList } from "./Home/MonthYearList";
import { toast } from "react-toastify";
import type { Batch } from "../../../shared/lib/types/dto/batch";
import { getCourseIds } from "../utils/getCourseIds";

export type User = {
  id: number;
  name: string;
  email?: string;
  mobileNumber?: string;
  gender: string;
  createdAt: string;
  updatedAt: string;
  highestQualification: keyof typeof QUALIFICATION_CODE;
  identityProofType: keyof typeof IDENTITY_PROOF_CODE;
  identityProofNumber: string;
  successfulPaymentRequest: Pick<
    PaymentRequest,
    "id" | "cscTransactionId" | "transactionStatus" | "invoiceUrl"
  >;
  highestQualificationFile: File & { downloadUrl: string };
  identityProofFile: File & { downloadUrl: string };
  candidatePhotoFile: File & { downloadUrl: string };
  candidateSignatureFile: File & { downloadUrl: string };
  district: string;
  state: string;
  vle: Pick<VLE, "id" | "name" | "cscId">;
  batchDetails?: Batch[];
};

const columns: (TableColumn<User> & {
  id?:
    | keyof User
    | "vleName"
    | "vleId"
    | "stateName"
    | "districtName"
    | "transactionNumber";
})[] = [
  {
    name: "Learner ID",
    selector: (row) => row.id,
    width: "8rem",
    wrap: true,
  },
  {
    name: "Name",
    selector: (row) => row.name,
    sortable: true,
    width: "14rem",
    wrap: true,
    id: "name",
  },
  {
    name: "Email",
    selector: (row) => row.email || "",
    sortable: true,
    width: "14rem",
    wrap: true,
    id: "email",
  },
  {
    name: "Mobile Number",
    selector: (row) => row.mobileNumber || "",
    sortable: true,
    width: "10rem",
    wrap: true,
    id: "mobileNumber",
  },
  {
    name: "Gender",
    selector: (row) => row.gender,
    sortable: true,
    wrap: true,
    id: "gender",
  },
  {
    name: "Registered At",
    selector: (row) => moment(row.createdAt).format("DD MMM Y, h:mm:s a"),
    sortable: true,
    width: "12rem",
    wrap: true,
    id: "createdAt",
  },
  {
    name: "Highest Qualification",
    sortable: true,
    selector: (row) => QUALIFICATION_NAME[row.highestQualification],
    wrap: true,
    id: "highestQualification",
  },
  {
    name: "Identity Proof Type",
    sortable: true,
    selector: (row) => IDENTITY_PROOF_NAME[row.identityProofType],
    width: "10rem",
    wrap: true,
    id: "identityProofType",
  },
  {
    name: "Identity Proof Number",
    sortable: true,
    selector: (row) => row.identityProofNumber,
    width: "12rem",
    wrap: true,
    id: "identityProofNumber",
  },
  {
    name: "Transaction Number",
    selector: (row) =>
      row.successfulPaymentRequest?.cscTransactionId?.toString() || "NA",
    sortable: true,
    width: "12rem",
    wrap: true,
    id: "transactionNumber",
  },
  {
    name: "Batch Name",
    sortable: true,
    selector: (row) =>
      row?.batchDetails?.map((batch) => batch.batchName).join(", ") || "",
    wrap: true,
    id: "batchDetails",
  },
  {
    name: "Language",
    sortable: true,
    selector: (row) =>
      row?.batchDetails?.map((batch) => batch.batchLanguage).join(", ") || "",
    wrap: true,
    id: "batchDetails",
  },
  {
    name: "District",
    sortable: true,
    selector: (row) => row.district,
    wrap: true,
    id: "districtName",
  },
  {
    name: "State",
    sortable: true,
    selector: (row) => row.state,
    wrap: true,
    id: "stateName",
  },
  {
    name: "Actions",
    cell: (row: any) => <UserListActionsFragment row={row} />,
    grow: 2,
    wrap: true,
    id: "updatedAt",
    width: "65rem",
  },
];
type Course = {
  value: number;
  label: string;
  batches: { id: number; batch_name: string }[];
};
export default function HomePage() {
  const {
    authState: { vle },
  } = useAuth();

  const navigate = useNavigate();

  const { setFieldValue } = useContext(
    FormContext
  ) as RegistrationFormContextShape;
  const [states, setStates] = useState<
    {
      name: string;
      pincodes: string[];
      districts: { name: string; pincodes: string[] }[];
    }[]
  >([]);

  const [districts, setDistricts] = useState<
    { name: string; pincodes: string[] }[]
  >([]);

  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<{
    value: number | undefined;
    label: string | null;
  } | null>(null);
  const [batches, setBatches] = useState<{ value: number; label: string }[]>(
    []
  );
  const [selectedBatches, setSelectedBatches] = useState<
    { value: number; label: string }[]
  >([]);
  const { result: userStats, loading: loadingUserStats } = usePromise(
    {
      fn: async () => {
        const vleCscId = vle?.cscId;
        if (vleCscId) {
          const response = await services.requestSvc.get<{
            vle: VLE;
            userOnboardingStats: UserOnboardingStats;
            confirmedUserCount: number;
            genderData: Record<string, number>;
          }>("/get-stats-by-vle?vleCscId=" + vleCscId);

          return {
            vle: response.vle,
            groupedData: response.userOnboardingStats,
          };
        }
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [vle]
  );

  const { data: searchParams, setValue: setSearchParams } = useURLState(
    {
      path: "searchParams",
      schema: GetCandidatesRequestInputDTO,
      initValue: {
        filter: {
          registrationStartDate: new Date(projectStartDate),
          registrationEndDate: new Date(),
        },
        sort: [],
        paginationParams: {
          pageIndex: 0,
          pageSize: 10,
        },
      },
      onError: (err) => {
        console.error(err);
        alert("An error occurred while parsing search parameters");
      },
    },
    []
  );

  const { filter, sort: sortParams, paginationParams } = searchParams || {};

  const setSearchParam = useCallback(
    <OKP extends ObjectKeyPath<typeof searchParams>>(
      changes: [OKP, ObjectKeyPathValueType<typeof searchParams, OKP>][]
    ) => {
      if (searchParams instanceof Object) {
        for (const change of changes) {
          setObjectValue(searchParams, change[0], change[1]);
        }
        setSearchParams({ ...searchParams });
      }
    },
    [searchParams, setSearchParams]
  );
  const [searchKeyword, setSearchKeyword] = useState(filter?.keywords || "");
  const course_ids = getCourseIds();

  const {
    result: searchUsersRes,
    error: searchUsersErr,
    loading: searchingUsers,
  } = usePromise(
    {
      fn: async () => {
        return await services.requestSvc.post<{
          data: User[];
          pagination: {
            currentPageIndex: number;
            pageSize: number;
            totalUsersCount: 0;
            totalPages: number;
          };
        }>("/users", { ...searchParams, courseIds: course_ids });
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [searchParams]
  );

  const usersData = searchUsersRes?.data;

  const userCount = searchUsersRes?.pagination.totalUsersCount;

  const stats = useMemo<{
    totalOnboarded: number;
    confirmedUsers: number;
    courseCompletionCount: number;
  }>(() => {
    if (userStats && filter)
      return calculateStatistics(userStats.groupedData, {
        fromData: new Date(projectStartDate),
        toDate: new Date(),
      });
    else
      return {
        totalOnboarded: 0,
        confirmedUsers: 0,
        courseCompletionCount: 0,
      };
  }, [userStats, filter]);

  const goToRegistrationForm = useCallback(() => {
    setFieldValue("isPersonalDetailsFinalised", false);
    navigate(PAGE_PATHS.SIGNUP);
  }, []);

  const { run: downloadCsv, loading: preparingCsv } = usePromise(
    {
      fn: async () => {
        if (!searchParams) throw new Error("Search Params not set");

        const res = await services.requestSvc.post<{
          downloadUrl: string;
        }>("/download-candidate-report", {
          ...searchParams.filter,
          courseIds: course_ids,
        });

        downloadFileFromURL(
          res.downloadUrl,
          res.downloadUrl.split("/").slice(-1)[0]
        );
      },
    },
    [searchParams]
  );

  console.log({ usersData });

  useEffect(() => {
    services.requestSvc
      .get<{
        states: {
          name: string;
          pincodes: string[];
          districts: { name: string; pincodes: string[] }[];
        }[];
      }>("/get-states-district-pincode-info")
      .then((response) => {
        setStates(response.states);
      })
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    services.requestSvc
      .post<{
        success: boolean;
        data: {
          id: number;
          full_name: string;
          batches: { id: number; batch_name: string }[];
        }[];
        message?: string;
      }>("/get-iibf-course-batches", {
        course_ids: course_ids,
      })
      .then((response) => {
        if (response.success) {
          const courseBatchOptionsInfo = response.data.map((course) => ({
            value: course.id,
            label: course.full_name,
            batches: course.batches,
          }));
          setCourses(courseBatchOptionsInfo);
          const allBatches = response.data.flatMap((course) =>
            course.batches.map((batch) => ({
              value: batch.id,
              label: batch.batch_name,
            }))
          );
          setBatches(allBatches);
        } else {
          toast.error(
            "Failed to fetch courses and batches: " + response.message
          );
        }
      })
      .catch((error: Error) => {
        toast.error("Something went wrong while fetching batches.");
      });
  }, []);

  useEffect(() => {
    if (searchUsersErr) {
      toast.error("Failed to load users. Please try again later.");
    }
  }, [searchUsersErr]);

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const stateName = event.target.value;
    setSelectedState(stateName);
    setSelectedDistrict("");

    const selectedStateObj = states?.find((s) => s.name === stateName);
    const pincodes = selectedStateObj?.pincodes || [];
    const districtsFromState = selectedStateObj?.districts || [];

    setDistricts(districtsFromState);

    setSearchParam([[["filter", "pincodes"], pincodes]]);
  };

  const handleDistrictChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const districtName = event.target.value;
    setSelectedDistrict(districtName);

    const selectedDistrictObj = districts.find((d) => d.name === districtName);
    const pincodes = selectedDistrictObj?.pincodes[0] || [];

    setSearchParam([[["filter", "pincodes"], [pincodes]]]);
  };
  const handleCourseChange = (
    selected: { value: number | undefined; label: string | null } | null
  ) => {
    setSelectedCourse(selected);
    setSelectedBatches([]);
    setSearchParam([[["filter", "batchIds"], []]]);
    setSearchParam([
      [["filter", "courseId"], selected ? selected.value : undefined],
    ]);
    setSearchParam([[["paginationParams", "pageIndex"], 0]]);
  };

  const handleBatchChange = (
    selected: readonly { value: number; label: string }[]
  ) => {
    const batchIds = selected.map((batch) => batch.value);
    setSelectedBatches([...selected]);
    setSearchParam([[["filter", "courseId"], []]]);
    setSearchParam([[["filter", "batchIds"], batchIds]]);
    setSearchParam([[["paginationParams", "pageIndex"], 0]]);
  };
  const getFilteredBatches = () => {
    if (selectedCourse && selectedCourse.value !== undefined) {
      return (
        courses
          .find((course) => course.value === selectedCourse.value)
          ?.batches.map((batch) => ({
            value: batch.id,
            label: batch.batch_name,
          })) || []
      );
    } else {
      return batches;
    }
  };
  return (
    <div className="min-h-screen bg-[#F2F6FC]">
      <Navbar />
      <div className="px-4 max-w-[1600px] mx-auto flex flex-col gap-y-8 mt-8">
        <div className="flex flex-wrap justify-between items-center rounded-lg mb-6 mt-4 w-full">
          <div className="text-lg font-semibold flex items-center gap-x-3">
            Total registered learners:
            <span className="text-3xl text-blue">{userCount}</span>
          </div>
          <button
            onClick={goToRegistrationForm}
            className="btn-primary w-full max-w-[300px]"
          >
            Register Candidate
          </button>
        </div>

        <div className="flex flex-col gap-y-5 mb-4">
          <div className="w-full flex flex-wrap gap-y-3 gap-x-16">
            <div className="flex space-x-4">
              <div>
                <label className="font-semibold">From: </label>
                <input
                  type="date"
                  value={moment(filter?.registrationStartDate).format(
                    "Y-MM-DD"
                  )}
                  onChange={(e) =>
                    setSearchParam([
                      [
                        ["filter", "registrationStartDate"],
                        new Date(e.target.value),
                      ],
                    ])
                  }
                  className="bg-white border border-gray-300 rounded-lg px-4 py-2"
                />
              </div>
              <div>
                <label className="font-semibold">To: </label>
                <input
                  type="date"
                  value={moment(filter?.registrationEndDate).format("Y-MM-DD")}
                  onChange={(e) =>
                    setSearchParam([
                      [
                        ["filter", "registrationEndDate"],
                        new Date(e.target.value),
                      ],
                    ])
                  }
                  className="bg-white border border-gray-300 rounded-lg px-4 py-2"
                />
              </div>
            </div>
            <div className="flex space-x-2 items-center">
              <span className="font-semibold">Search:</span>
              <input
                type="text"
                value={searchKeyword}
                placeholder="Enter keywords"
                className="border rounded px-4 py-2 w-full"
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
              <button
                onClick={() =>
                  setSearchParam([[["filter", "keywords"], searchKeyword]])
                }
                className="btn-primary"
              >
                Search
              </button>
            </div>
          </div>
          {filter?.registrationEndDate && filter.registrationStartDate && (
            <div className="flex flex-wrap gap-x-2 gap-y-3 items-center">
              <span className="font-semibold">Quick select:</span>
              <MonthYearList
                startDate={new Date(projectStartDate)}
                endDate={new Date()}
                onChange={(startDate, endDate) =>
                  setSearchParam([
                    [["filter", "registrationStartDate"], startDate],
                    [["filter", "registrationEndDate"], endDate],
                  ])
                }
                selectedDateRangeStart={
                  searchParams?.filter.registrationStartDate
                }
                selectedDateRangeEnd={searchParams?.filter.registrationEndDate}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col justify-between mb-2 mt-2">
          <div className="flex gap-2">
            <Select
              options={[
                { value: undefined, label: "Select a Course" },
                ...courses.map((course) => ({
                  value: course.value,
                  label: course.label,
                })),
              ]}
              value={selectedCourse}
              onChange={handleCourseChange}
              getOptionLabel={(e) => e.label || ""}
              getOptionValue={(e) =>
                e.value === undefined ? "" : e.value.toString()
              }
              placeholder="Select a course"
              className="w-1/3"
            />

            <Select
              isMulti
              options={getFilteredBatches()}
              value={selectedBatches}
              onChange={handleBatchChange}
              getOptionLabel={(e) => e.label}
              getOptionValue={(e) => e.value.toString()}
              placeholder="Select batches"
              closeMenuOnSelect={false}
              className="w-1/3"
            />
          </div>
          <div className="flex gap-x-2 mt-4">
            <select
              value={selectedState}
              onChange={handleStateChange}
              className="border p-2 rounded"
            >
              <option value="">Select State</option>
              {states?.map((state) => (
                <option key={state.name} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>

            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              className="border p-2 rounded"
              disabled={!selectedState}
            >
              <option value="">Select District</option>
              {districts.map((district) => (
                <option key={district.name} value={district.name}>
                  {district.name}
                </option>
              ))}
            </select>
            <button
              onClick={downloadCsv}
              className="btn-primary"
              disabled={preparingCsv}
            >
              {preparingCsv ? "Preparing CSV..." : "Download CSV"}
            </button>
          </div>
        </div>

        <div className="">
          <DataTable
            columns={columns}
            defaultSortFieldId={sortParams?.[0]?.column}
            progressPending={searchingUsers}
            data={usersData || []}
            paginationDefaultPage={(paginationParams?.pageIndex || 0) + 1}
            pagination
            paginationServer
            paginationRowsPerPageOptions={[2, 5, 10, 15, 30, 60]}
            paginationTotalRows={userCount}
            paginationPerPage={paginationParams?.pageSize}
            onChangePage={(targetPageNumber) =>
              setSearchParam([
                [["paginationParams", "pageIndex"], targetPageNumber - 1],
              ])
            }
            onChangeRowsPerPage={(newCount) => {
              setSearchParam([
                [["paginationParams", "pageIndex"], 0],
                [["paginationParams", "pageSize"], newCount],
              ]);
            }}
            sortServer
            onSort={(column, order) => {
              setSearchParam([[["sort"], [{ column: column.id, order }]]]);
            }}
            customStyles={{
              table: {
                style: {
                  overflow: "hidden",
                  borderRadius: "8px 8px 0px 0px",
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderLeft: "0",
                },
              },
              headRow: {
                style: {
                  background: "#b1c7e9",
                  color: "#3D3D3D",
                  fontWeight: "600",
                  "&:hover": {
                    cursor: "pointer",
                  },
                },
              },
              headCells: {
                style: {
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderTop: "0",
                  borderRight: "0",
                  borderBottom: "0",
                },
              },
              cells: {
                style: {
                  borderLeft: "1px solid rgba(0,0,0,0.4)",
                },
              },
              rows: {
                style: {
                  backgroundColor: "#fff",
                  cursor: "default",
                  color: "#656363",
                  fontSize: "14px",
                  "&:hover": {
                    cursor: "default",
                    backgroundColor: "#E9E9E9",
                  },
                  borderBottom: "1px solid rgba(0,0,0,0.4)",
                },
              },
              pagination: {
                style: {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "0.375rem",
                  borderTopLeftRadius: "0px",
                  borderTopRightRadius: "0px",
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderTop: "0",
                  fontWeight: "500",
                  color: "rgb(61, 61, 61)",
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
